import React, { useState, useEffect } from 'react';
import apiService, { errorHelpers, formatHelpers } from '../../../utils/apiService';

const VerificationReports = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [dateRange, setDateRange] = useState({
        date_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
        date_to: new Date().toISOString().split('T')[0] // today
    });

    // State for different data types
    const [analyticsData, setAnalyticsData] = useState(null);
    const [authenticationsData, setAuthenticationsData] = useState([]);
    const [transactionsData, setTransactionsData] = useState([]);
    const [authPagination, setAuthPagination] = useState({});
    const [transactionsPagination, setTransactionsPagination] = useState({});
    const [currentAuthPage, setCurrentAuthPage] = useState(1);
    const [currentTransPage, setCurrentTransPage] = useState(1);

    // Filters
    const [authFilters, setAuthFilters] = useState({
        status: '',
        date_from: dateRange.date_from,
        date_to: dateRange.date_to
    });
    const [transFilters, setTransFilters] = useState({
        status: '',
        transaction_id: '',
        date_from: dateRange.date_from,
        date_to: dateRange.date_to
    });

    // Error and success states
    const [error, setError] = useState('');
    const [reconciling, setReconciling] = useState(false);

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        if (activeTab === 'authentications') {
            loadAuthentications();
        } else if (activeTab === 'transactions') {
            loadTransactions();
        }
    }, [activeTab, currentAuthPage, currentTransPage, authFilters, transFilters]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            setError('');

            // Load dashboard analytics
            const analyticsResponse = await apiService.admin.getDashboardAnalytics();
            setAnalyticsData(analyticsResponse.data.analytics);

            // Load initial authentications data
            await loadAuthentications();

        } catch (err) {
            console.error('Error loading initial data:', err);
            setError(errorHelpers.getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const loadAuthentications = async () => {
        try {
            const params = {
                page: currentAuthPage,
                per_page: 20,
                ...authFilters
            };

            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '') delete params[key];
            });

            const response = await apiService.admin.getAuthentications(params);
            setAuthenticationsData(response.data.authentications);
            setAuthPagination(response.data.pagination);
        } catch (err) {
            console.error('Error loading authentications:', err);
            setError(errorHelpers.getErrorMessage(err));
        }
    };

    const loadTransactions = async () => {
        try {
            const params = {
                limit: 50,
                ...transFilters
            };

            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '') delete params[key];
            });

            const response = await apiService.verification.searchTransactions(params);
            setTransactionsData(response.data.transactions);
            setTransactionsPagination({
                total: response.data.count,
                page: currentTransPage,
                per_page: 50
            });
        } catch (err) {
            console.error('Error loading transactions:', err);
            setError(errorHelpers.getErrorMessage(err));
        }
    };

    const handleBulkReconciliation = async () => {
        try {
            setReconciling(true);
            setError('');

            const response = await apiService.admin.bulkCheckPayments();
            alert(`Bulk reconciliation completed. Processed ${response.data.processed_count} transactions.`);

            // Reload data
            await loadInitialData();
        } catch (err) {
            console.error('Error during bulk reconciliation:', err);
            setError(errorHelpers.getErrorMessage(err));
        } finally {
            setReconciling(false);
        }
    };

    const handleSingleReconcile = async (transactionId) => {
        try {
            await apiService.verification.reconcileSinglePayment(transactionId);
            alert(`Transaction ${transactionId} reconciled successfully.`);

            // Reload current tab data
            if (activeTab === 'authentications') {
                await loadAuthentications();
            } else if (activeTab === 'transactions') {
                await loadTransactions();
            }
        } catch (err) {
            console.error('Error reconciling transaction:', err);
            alert(errorHelpers.getErrorMessage(err));
        }
    };

    const exportAuthenticationsToCSV = () => {
        if (!authenticationsData.length) return;

        const headers = [
            'ID', 'User Email', 'User Name', 'Certificate Number',
            'Payment Status', 'Amount Paid', 'Payment Reference',
            'Authentication Result', 'Date', 'BPMS Transaction ID'
        ];

        const csvRows = [
            headers.join(','),
            ...authenticationsData.map(auth => [
                auth.id,
                auth.user?.email || 'N/A',
                `"${auth.user?.firstname || ''} ${auth.user?.lastname || ''}"`,
                auth.certificate_number,
                auth.payment_status,
                auth.amount_paid || 0,
                auth.payment_reference || 'N/A',
                auth.authentication_result || 'N/A',
                formatHelpers.formatDateTime(auth.authenticated_at),
                auth.bpms_transaction_id || 'N/A'
            ].join(','))
        ];

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `authentications_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const exportTransactionsToCSV = () => {
        if (!transactionsData.length) return;

        const headers = [
            'Transaction ID', 'Reg Number', 'Student Name', 'Email',
            'Currency', 'Amount Due', 'Amount Paid', 'Status',
            'Created At', 'Updated At'
        ];

        const csvRows = [
            headers.join(','),
            ...transactionsData.map(trans => [
                trans.transid,
                trans.regnum || 'N/A',
                `"${trans.sname || ''} ${trans.fname || ''}"`,
                trans.email || 'N/A',
                trans.currency || 'NGN',
                trans.amount_due || 0,
                trans.amount_paid || 0,
                trans.status_desc || 'N/A',
                formatHelpers.formatDateTime(trans.created_at),
                formatHelpers.formatDateTime(trans.updated_at)
            ].join(','))
        ];

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const renderOverviewTab = () => (
        <div className="overview-tab">
            {analyticsData && (
                <div className="analytics-grid">
                    <div className="analytics-cards">
                        <div className="card">
                            <h3>👥 Total Users</h3>
                            <div className="metric">{formatHelpers.formatNumber(analyticsData.total_users)}</div>
                            <small>+{analyticsData.recent_users} in last 30 days</small>
                        </div>

                        <div className="card">
                            <h3>📜 Total Certificates</h3>
                            <div className="metric">{formatHelpers.formatNumber(analyticsData.total_certificates)}</div>
                        </div>

                        <div className="card">
                            <h3>✅ Total Verifications</h3>
                            <div className="metric">{formatHelpers.formatNumber(analyticsData.total_authentications)}</div>
                            <small>+{analyticsData.recent_authentications} in last 30 days</small>
                        </div>

                        <div className="card">
                            <h3>💰 Total Revenue</h3>
                            <div className="metric">{formatHelpers.formatCurrency(analyticsData.total_revenue)}</div>
                            <small>+{formatHelpers.formatCurrency(analyticsData.recent_revenue)} in last 30 days</small>
                        </div>

                        <div className="card">
                            <h3>📊 Success Rate</h3>
                            <div className="metric">{analyticsData.success_rate}%</div>
                        </div>
                    </div>

                    <div className="bulk-actions">
                        <h3>🔄 Bulk Operations</h3>
                        <button
                            className="btn btn-primary"
                            onClick={handleBulkReconciliation}
                            disabled={reconciling}
                        >
                            {reconciling ? 'Reconciling...' : 'Bulk Reconcile Payments'}
                        </button>
                        <p className="help-text">
                            Reconcile all pending payments with BPMS system to sync payment statuses.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );

    const renderAuthenticationsTab = () => (
        <div className="authentications-tab">
            <div className="tab-header">
                <h3>🔐 Authentication Records</h3>
                <div className="tab-actions">
                    <button className="btn btn-secondary" onClick={exportAuthenticationsToCSV}>
                        📊 Export CSV
                    </button>
                </div>
            </div>

            <div className="filters">
                <div className="filter-group">
                    <select
                        value={authFilters.status}
                        onChange={(e) => setAuthFilters({ ...authFilters, status: e.target.value })}
                    >
                        <option value="">All Statuses</option>
                        <option value="success">Success</option>
                        <option value="failed">Failed</option>
                        <option value="pending">Pending</option>
                    </select>

                    <input
                        type="date"
                        value={authFilters.date_from}
                        onChange={(e) => setAuthFilters({ ...authFilters, date_from: e.target.value })}
                        placeholder="From Date"
                    />

                    <input
                        type="date"
                        value={authFilters.date_to}
                        onChange={(e) => setAuthFilters({ ...authFilters, date_to: e.target.value })}
                        placeholder="To Date"
                    />

                    <button className="btn btn-primary" onClick={loadAuthentications}>
                        🔍 Filter
                    </button>
                </div>
            </div>

            <div className="table-container">
                <table className="table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>User</th>
                            <th>Certificate #</th>
                            <th>Payment Status</th>
                            <th>Amount</th>
                            <th>Result</th>
                            <th>Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {authenticationsData.map(auth => (
                            <tr key={auth.id}>
                                <td>{auth.id}</td>
                                <td>
                                    <div className="user-info">
                                        <strong>{auth.user?.firstname} {auth.user?.lastname}</strong>
                                        <br />
                                        <small>{auth.user?.email}</small>
                                    </div>
                                </td>
                                <td>
                                    <code>{auth.certificate_number}</code>
                                </td>
                                <td>
                                    <span className={`status-badge status-${formatHelpers.formatPaymentStatusColor(auth.payment_status)}`}>
                                        {formatHelpers.formatPaymentStatus(auth.payment_status)}
                                    </span>
                                </td>
                                <td>
                                    {formatHelpers.formatCurrency(auth.amount_paid)}
                                </td>
                                <td>
                                    <span className={`status-badge status-${auth.authentication_result === 'success' ? 'green' : 'red'}`}>
                                        {auth.authentication_result || 'N/A'}
                                    </span>
                                </td>
                                <td>
                                    {formatHelpers.formatDateTime(auth.authenticated_at)}
                                </td>
                                <td>
                                    {auth.bpms_transaction_id && (
                                        <button
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => handleSingleReconcile(auth.bpms_transaction_id)}
                                            title="Reconcile with BPMS"
                                        >
                                            🔄
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {authPagination.pages > 1 && (
                <div className="pagination">
                    <button
                        className="btn btn-secondary"
                        disabled={!authPagination.has_prev}
                        onClick={() => setCurrentAuthPage(currentAuthPage - 1)}
                    >
                        Previous
                    </button>
                    <span>Page {authPagination.page} of {authPagination.pages}</span>
                    <button
                        className="btn btn-secondary"
                        disabled={!authPagination.has_next}
                        onClick={() => setCurrentAuthPage(currentAuthPage + 1)}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );

    const renderTransactionsTab = () => (
        <div className="transactions-tab">
            <div className="tab-header">
                <h3>💳 BPMS Transactions</h3>
                <div className="tab-actions">
                    <button className="btn btn-secondary" onClick={exportTransactionsToCSV}>
                        📊 Export CSV
                    </button>
                </div>
            </div>

            <div className="filters">
                <div className="filter-group">
                    <input
                        type="text"
                        placeholder="Transaction ID"
                        value={transFilters.transaction_id}
                        onChange={(e) => setTransFilters({ ...transFilters, transaction_id: e.target.value })}
                    />

                    <select
                        value={transFilters.status}
                        onChange={(e) => setTransFilters({ ...transFilters, status: e.target.value })}
                    >
                        <option value="">All Statuses</option>
                        <option value="PAID">PAID</option>
                        <option value="PENDING">PENDING</option>
                        <option value="FAILED">FAILED</option>
                    </select>

                    <input
                        type="date"
                        value={transFilters.date_from}
                        onChange={(e) => setTransFilters({ ...transFilters, date_from: e.target.value })}
                        placeholder="From Date"
                    />

                    <input
                        type="date"
                        value={transFilters.date_to}
                        onChange={(e) => setTransFilters({ ...transFilters, date_to: e.target.value })}
                        placeholder="To Date"
                    />

                    <button className="btn btn-primary" onClick={loadTransactions}>
                        🔍 Filter
                    </button>
                </div>
            </div>

            <div className="table-container">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Transaction ID</th>
                            <th>Student</th>
                            <th>Email</th>
                            <th>Amount Due</th>
                            <th>Amount Paid</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {transactionsData.map(trans => (
                            <tr key={trans.transid}>
                                <td>
                                    <code>{trans.transid}</code>
                                </td>
                                <td>
                                    {trans.sname} {trans.fname}
                                </td>
                                <td>
                                    {trans.email}
                                </td>
                                <td>
                                    {formatHelpers.formatCurrency(trans.amount_due, trans.currency)}
                                </td>
                                <td>
                                    {formatHelpers.formatCurrency(trans.amount_paid, trans.currency)}
                                </td>
                                <td>
                                    <span className={`status-badge status-${formatHelpers.formatPaymentStatusColor(trans.status_desc)}`}>
                                        {formatHelpers.formatPaymentStatus(trans.status_desc)}
                                    </span>
                                </td>
                                <td>
                                    {formatHelpers.formatDateTime(trans.created_at)}
                                </td>
                                <td>
                                    <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => handleSingleReconcile(trans.transid)}
                                        title="Reconcile Transaction"
                                    >
                                        🔄
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="pagination-info">
                <small>Showing {transactionsData.length} of {transactionsPagination.total} transactions</small>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="verification-reports">
                <h2>📈 Verification Reports</h2>
                <div className="loading">Loading reports data...</div>
            </div>
        );
    }

    return (
        <div className="verification-reports">
            <div className="reports-header">
                <h2>📈 Verification Reports</h2>
                {error && (
                    <div className="alert alert-danger">
                        ⚠️ {error}
                    </div>
                )}
            </div>

            <div className="reports-tabs">
                <div className="tab-nav">
                    <button
                        className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        📊 Overview
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'authentications' ? 'active' : ''}`}
                        onClick={() => setActiveTab('authentications')}
                    >
                        🔐 Authentications
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'transactions' ? 'active' : ''}`}
                        onClick={() => setActiveTab('transactions')}
                    >
                        💳 BPMS Transactions
                    </button>
                </div>

                <div className="tab-content">
                    {activeTab === 'overview' && renderOverviewTab()}
                    {activeTab === 'authentications' && renderAuthenticationsTab()}
                    {activeTab === 'transactions' && renderTransactionsTab()}
                </div>
            </div>

            <style jsx>{`
                .verification-reports {
                    padding: 20px;
                }

                .reports-header {
                    margin-bottom: 30px;
                }

                .reports-header h2 {
                    margin: 0 0 15px 0;
                    color: #333;
                }

                .alert {
                    padding: 12px 16px;
                    border-radius: 6px;
                    margin-bottom: 20px;
                }

                .alert-danger {
                    background-color: #fee;
                    border: 1px solid #fcc;
                    color: #c33;
                }

                .loading {
                    text-align: center;
                    padding: 40px;
                    color: #666;
                }

                .tab-nav {
                    display: flex;
                    gap: 4px;
                    margin-bottom: 20px;
                    border-bottom: 2px solid #eee;
                }

                .tab-btn {
                    padding: 12px 20px;
                    border: none;
                    background: none;
                    cursor: pointer;
                    border-bottom: 3px solid transparent;
                    font-weight: 500;
                    color: #666;
                    transition: all 0.2s;
                }

                .tab-btn:hover {
                    color: #333;
                    background-color: #f8f9fa;
                }

                .tab-btn.active {
                    color: #007bff;
                    border-bottom-color: #007bff;
                    background-color: #f8f9fa;
                }

                .analytics-grid {
                    display: grid;
                    gap: 30px;
                    grid-template-columns: 2fr 1fr;
                }

                .analytics-cards {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
                    gap: 20px;
                }

                .card {
                    background: white;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    padding: 20px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .card h3 {
                    margin: 0 0 10px 0;
                    font-size: 14px;
                    color: #666;
                    font-weight: 600;
                }

                .card .metric {
                    font-size: 28px;
                    font-weight: bold;
                    color: #333;
                    margin-bottom: 5px;
                }

                .card small {
                    color: #28a745;
                    font-size: 12px;
                }

                .bulk-actions {
                    background: white;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    padding: 20px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .bulk-actions h3 {
                    margin: 0 0 15px 0;
                    color: #333;
                }

                .help-text {
                    margin: 10px 0 0 0;
                    font-size: 14px;
                    color: #666;
                }

                .tab-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }

                .tab-header h3 {
                    margin: 0;
                    color: #333;
                }

                .tab-actions {
                    display: flex;
                    gap: 10px;
                }

                .filters {
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    padding: 15px;
                    margin-bottom: 20px;
                }

                .filter-group {
                    display: flex;
                    gap: 10px;
                    align-items: center;
                    flex-wrap: wrap;
                }

                .filter-group input,
                .filter-group select {
                    padding: 8px 12px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 14px;
                }

                .table-container {
                    background: white;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    overflow: hidden;
                    margin-bottom: 20px;
                }

                .table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 0;
                }

                .table th,
                .table td {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #eee;
                }

                .table th {
                    background-color: #f8f9fa;
                    font-weight: 600;
                    color: #333;
                    font-size: 14px;
                }

                .table td {
                    font-size: 14px;
                }

                .table tbody tr:hover {
                    background-color: #f8f9fa;
                }

                .user-info strong {
                    display: block;
                }

                .user-info small {
                    color: #666;
                }

                .status-badge {
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                    text-transform: uppercase;
                }

                .status-green {
                    background-color: #d4edda;
                    color: #155724;
                }

                .status-red {
                    background-color: #f8d7da;
                    color: #721c24;
                }

                .status-orange {
                    background-color: #fff3cd;
                    color: #856404;
                }

                .status-gray {
                    background-color: #e2e3e5;
                    color: #383d41;
                }

                .status-blue {
                    background-color: #cce7ff;
                    color: #004085;
                }

                .pagination {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 15px;
                    margin-top: 20px;
                }

                .pagination-info {
                    text-align: center;
                    margin-top: 10px;
                }

                .pagination-info small {
                    color: #666;
                }

                .btn {
                    padding: 8px 16px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    background: white;
                    cursor: pointer;
                    font-size: 14px;
                    text-decoration: none;
                    display: inline-block;
                    transition: all 0.2s;
                }

                .btn:hover {
                    background-color: #f8f9fa;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-primary {
                    background-color: #007bff;
                    border-color: #007bff;
                    color: white;
                }

                .btn-primary:hover:not(:disabled) {
                    background-color: #0056b3;
                    border-color: #0056b3;
                }

                .btn-secondary {
                    background-color: #6c757d;
                    border-color: #6c757d;
                    color: white;
                }

                .btn-secondary:hover:not(:disabled) {
                    background-color: #545b62;
                    border-color: #545b62;
                }

                .btn-sm {
                    padding: 4px 8px;
                    font-size: 12px;
                }

                code {
                    background-color: #f8f9fa;
                    border: 1px solid #e9ecef;
                    border-radius: 3px;
                    padding: 2px 4px;
                    font-family: 'Courier New', monospace;
                    font-size: 12px;
                }

                @media (max-width: 768px) {
                    .analytics-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .analytics-cards {
                        grid-template-columns: 1fr;
                    }
                    
                    .filter-group {
                        flex-direction: column;
                        align-items: stretch;
                    }
                    
                    .tab-header {
                        flex-direction: column;
                        gap: 15px;
                        align-items: stretch;
                    }
                    
                    .table-container {
                        overflow-x: auto;
                    }
                }
            `}</style>
        </div>
    );
};

export default VerificationReports;